import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CargoheatingCommonService } from '../../service/cargo-heating-service/cargoheating-common.service';
import {
  validateVoyageLogUpload,
  validateVoyageLogUploadFormat,
} from '../../helpers/voyage-log-upload-validation.util';

@Component({
  selector: 'app-complete-voayage',
  templateUrl: './complete-voayage.component.html',
  styleUrls: ['./complete-voayage.component.scss'],
})
export class CompleteVoayageComponent {
  selectedFile: File | null = null;
  isUploading = false;
  isValidatingFile = false;
  fileValidationPassed = false;
  validationErrors: string[] = [];
  voyageId: string;
  vesselName?: string;
  vesselImo?: string;

  constructor(
    private dialogRef: MatDialogRef<CompleteVoayageComponent>,
    private toastr: ToastrService,
    private commonService: CargoheatingCommonService,
    @Inject(MAT_DIALOG_DATA)
    public data: { voyageId?: string; vesselName?: string; vesselImo?: string },
  ) {
    this.voyageId = data?.voyageId || '';
    this.vesselName = data?.vesselName;
    this.vesselImo = data?.vesselImo;
  }

  get canSubmit(): boolean {
    return !!this.selectedFile && this.fileValidationPassed && !this.isValidatingFile;
  }

  uploadFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xls,.xlsx,.xlsm,.csv';

    input.onchange = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      void this.applySelectedFile(file);
    };

    input.click();
  }

  async submitFile(): Promise<void> {
    if (!this.selectedFile) {
      this.toastr.warning('Please select a file first');
      return;
    }

    if (!this.voyageId) {
      this.toastr.error('Voyage ID is missing');
      return;
    }

    const validation = await validateVoyageLogUpload(this.selectedFile, {
      vesselName: this.vesselName,
      vesselImo: this.vesselImo,
    });

    if (!validation.valid) {
      this.validationErrors = validation.errors;
      this.toastr.error(
        validation.errors[0] || 'File validation failed. Fix the issues and try again.',
      );
      return;
    }

    this.isUploading = true;

    const uploadPayload = {
      voyage_id: this.voyageId,
    };

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('json_data', JSON.stringify(uploadPayload));

    this.commonService.processVoyageLogs(formData).subscribe({
      next: () => {
        const statusPayload = {
          voyage_id: this.voyageId,
          voyage_status: 'Completed',
        };

        this.commonService.changeVoyageStatus(statusPayload).subscribe({
          next: () => {
            this.isUploading = false;
            this.toastr.success(
              'Temperature log uploaded & Voyage marked Completed',
            );
            this.dialogRef.close({ success: true });
          },
          error: (err: { error?: { message?: string } }) => {
            this.isUploading = false;
            this.toastr.error(
              err?.error?.message ||
                'File uploaded but failed to mark voyage as Completed',
            );
          },
        });
      },
      error: (err: { error?: { message?: string } }) => {
        this.isUploading = false;
        this.toastr.error(err?.error?.message || 'File upload failed');
      },
    });
  }

  closeModal(): void {
    if (this.isUploading || this.isValidatingFile) {
      this.toastr.info('Please wait for upload to complete');
      return;
    }
    this.dialogRef.close({ success: false });
  }

  private async applySelectedFile(file: File): Promise<void> {
    const formatResult = validateVoyageLogUploadFormat(file);
    if (!formatResult.valid) {
      this.resetValidationState();
      this.selectedFile = null;
      this.toastr.error(formatResult.errors[0] || 'Invalid file selected.');
      return;
    }

    this.selectedFile = file;
    this.resetValidationState();
    this.isValidatingFile = true;

    const validation = await validateVoyageLogUpload(file, {
      vesselName: this.vesselName,
      vesselImo: this.vesselImo,
    });

    this.isValidatingFile = false;
    this.validationErrors = validation.errors;
    this.fileValidationPassed = validation.valid;

    if (!validation.valid) {
      this.toastr.error(
        validation.errors[0] || 'File validation failed. Review the issues below.',
      );
      return;
    }

    if (validation.warnings.length) {
      this.toastr.warning(validation.warnings[0]);
    } else {
      this.toastr.success(
        `File validated (${validation.rowCount} row${validation.rowCount === 1 ? '' : 's'}).`,
      );
    }
  }

  private resetValidationState(): void {
    this.validationErrors = [];
    this.fileValidationPassed = false;
  }
}
